import { useEffect, useState, useCallback } from "react";
import {
  BarChart3, Eye, Play, Users, Globe, Clock, TrendingUp,
  RefreshCw, ChevronDown, Link, Share2, Copy
} from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "../../context/AuthContext";

export const EmbedAnalytics = ({ authToken, apiKey }) => {
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const headers = useCallback(() => {
    const h = { "Content-Type": "application/json" };
    if (authToken) h["Authorization"] = `Bearer ${authToken}`;
    return h;
  }, [authToken]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const h = headers();
    try {
      const [sumRes, tlRes, refRes, phRes, rtRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/embed-analytics/summary?days=${days}`, { headers: h }),
        fetch(`${BACKEND_URL}/api/embed-analytics/timeline?days=${days}`, { headers: h }),
        fetch(`${BACKEND_URL}/api/embed-analytics/referrers?days=${days}`, { headers: h }),
        fetch(`${BACKEND_URL}/api/embed-analytics/peak-hours?days=${days}`, { headers: h }),
        fetch(`${BACKEND_URL}/api/embed-analytics/realtime`, { headers: h }),
      ]);
      const [sumData, tlData, refData, phData, rtData] = await Promise.all([
        sumRes.json(), tlRes.json(), refRes.json(), phRes.json(), rtRes.json()
      ]);
      setSummary(sumData);
      setTimeline(tlData);
      setReferrers(refData);
      setPeakHours(phData);
      setRealtime(rtData);
    } catch (err) {
      console.error("Analytics fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [headers, days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetch(`${BACKEND_URL}/api/embed-analytics/realtime`, { headers: headers() })
        .then(r => r.json())
        .then(setRealtime)
        .catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [headers]);

  const hasData = summary && (summary.total_impressions > 0 || summary.total_plays > 0);
  const maxTimelineVal = Math.max(...timeline.map(d => Math.max(d.impressions, d.plays, d.listeners)), 1);
  const maxHourVal = Math.max(...peakHours.map(h => h.listeners), 1);

  const siteUrl = "https://djsandmcmedia.com";
  const embedCode = apiKey
    ? `<iframe src="${siteUrl}/api/embed/${apiKey}/full-player" style="width:100%;height:100vh;min-height:700px;border:none" allow="autoplay" loading="lazy"></iframe>`
    : "";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="embed-analytics-loading">
        <RefreshCw className="w-5 h-5 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="embed-analytics">
      {/* Section Header — "Where Your Listeners Come From" */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <BarChart3 className="w-5 h-5 text-purple-400" />
            <h3 className="text-lg font-bold text-white">Where Your Listeners Come From</h3>
          </div>
          <div className="flex items-center gap-4 ml-8">
            <div className="flex items-center gap-1.5 text-white/30 text-xs">
              <Globe className="w-3.5 h-3.5" /> Websites
            </div>
            <div className="flex items-center gap-1.5 text-white/30 text-xs">
              <Share2 className="w-3.5 h-3.5" /> Social
            </div>
            <div className="flex items-center gap-1.5 text-white/30 text-xs">
              <Link className="w-3.5 h-3.5" /> Direct Links
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
              className="bg-white/5 border border-white/10 rounded-lg pl-3 pr-8 py-1.5 text-sm text-white appearance-none cursor-pointer"
              data-testid="embed-analytics-period-select">
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-white/40 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          <button onClick={fetchAll}
            className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg p-1.5 transition"
            data-testid="embed-analytics-refresh">
            <RefreshCw className="w-4 h-4 text-white/50" />
          </button>
        </div>
      </div>

      {/* Realtime Banner */}
      {realtime && realtime.active_listeners > 0 && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="embed-analytics-realtime">
          <div className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
          <span className="text-green-400 text-sm font-medium">
            {realtime.active_listeners} active listener{realtime.active_listeners !== 1 ? "s" : ""} right now
          </span>
          {realtime.active_domains.length > 0 && (
            <span className="text-green-400/50 text-xs ml-auto hidden sm:inline">
              on {realtime.active_domains.slice(0, 3).join(", ")}
            </span>
          )}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Impressions", value: hasData ? summary.total_impressions : null, ghost: "1,247", icon: Eye, color: "text-blue-400", bg: "bg-blue-500/10" },
          { label: "Plays", value: hasData ? summary.total_plays : null, ghost: "834", icon: Play, color: "text-green-400", bg: "bg-green-500/10" },
          { label: "Unique Listeners", value: hasData ? summary.unique_listeners : null, ghost: "312", icon: Users, color: "text-purple-400", bg: "bg-purple-500/10" },
          { label: "Sessions", value: hasData ? summary.unique_sessions : null, ghost: "1,089", icon: TrendingUp, color: "text-amber-400", bg: "bg-amber-500/10" },
        ].map((stat) => (
          <div key={stat.label} className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`embed-stat-${stat.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-lg ${stat.bg} flex items-center justify-center`}>
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
              </div>
            </div>
            {stat.value !== null ? (
              <div className="text-2xl font-bold text-white">{stat.value.toLocaleString()}</div>
            ) : (
              <div className="text-2xl font-bold text-white/10">{stat.ghost}</div>
            )}
            <div className="text-white/40 text-xs mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Empty State — Motivational with CTA */}
      {!hasData && (
        <div className="bg-gradient-to-br from-purple-500/5 to-indigo-500/5 border border-purple-500/15 rounded-xl p-6 text-center" data-testid="embed-analytics-empty">
          <p className="text-white/50 text-sm font-medium">
            Once your player is embedded, you'll see exactly where your listeners are coming from.
          </p>
          <p className="text-white/25 text-xs mt-2 max-w-md mx-auto">
            Every play, every impression, every website — tracked in real-time so you know what's working.
          </p>
          {embedCode && (
            <button onClick={() => { navigator.clipboard.writeText(embedCode); toast.success("Embed code copied!"); }}
              className="mt-4 inline-flex items-center gap-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/20 text-purple-400 px-5 py-2.5 rounded-xl text-xs font-medium transition"
              data-testid="embed-analytics-copy-cta">
              <Copy className="w-3.5 h-3.5" /> Copy Embed Code to Get Started
            </button>
          )}
        </div>
      )}

      {/* Timeline + Referrers + Peak Hours — only show when we have real data */}
      {hasData && (
        <>
          {/* Timeline Chart */}
          {timeline.length > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h4 className="text-sm font-medium text-white/60 mb-4">Daily Activity</h4>
              <div className="flex items-end gap-1 h-32" data-testid="embed-analytics-timeline">
                {timeline.map((d, i) => {
                  const barH = Math.max((d.plays / maxTimelineVal) * 100, 4);
                  const impH = Math.max((d.impressions / maxTimelineVal) * 100, 2);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative" title={`${d.date}\n${d.plays} plays, ${d.impressions} impressions`}>
                      <div className="w-full rounded-t" style={{ height: `${impH}%`, background: "rgba(96,165,250,0.2)" }} />
                      <div className="w-full rounded-t" style={{ height: `${barH}%`, background: "linear-gradient(to top, #8B5CF6, #A78BFA)" }} />
                      <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white whitespace-nowrap z-10">
                        {d.date}: {d.plays} plays, {d.impressions} imp
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-3">
                <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                  <div className="w-3 h-2 rounded-sm" style={{ background: "#8B5CF6" }} /> Plays
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                  <div className="w-3 h-2 rounded-sm" style={{ background: "rgba(96,165,250,0.3)" }} /> Impressions
                </div>
              </div>
            </div>
          )}

          {/* Referrers + Peak Hours side by side */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-4 h-4 text-blue-400" />
                <h4 className="text-sm font-medium text-white/60">Top Referrers</h4>
              </div>
              {referrers.length === 0 ? (
                <p className="text-white/20 text-xs text-center py-6">No referrer data yet</p>
              ) : (
                <div className="space-y-2" data-testid="embed-analytics-referrers">
                  {referrers.map((ref, i) => {
                    const maxPlays = Math.max(...referrers.map(r => r.plays), 1);
                    return (
                      <div key={i} className="relative">
                        <div className="absolute inset-0 rounded-lg" style={{
                          width: `${Math.max((ref.plays / maxPlays) * 100, 5)}%`,
                          background: "rgba(139,92,246,0.08)"
                        }} />
                        <div className="relative flex items-center justify-between px-3 py-2">
                          <span className="text-white/70 text-xs font-mono truncate max-w-[55%]">{ref.domain}</span>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="text-green-400">{ref.plays} <span className="text-white/30">plays</span></span>
                            <span className="text-blue-400/60">{ref.unique_listeners} <span className="text-white/20">uniq</span></span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-amber-400" />
                <h4 className="text-sm font-medium text-white/60">Peak Listening Hours</h4>
              </div>
              <div className="flex items-end gap-0.5 h-24" data-testid="embed-analytics-peak-hours">
                {peakHours.map((h, i) => {
                  const barH = Math.max((h.listeners / maxHourVal) * 100, 3);
                  const isPeak = h.listeners === maxHourVal && maxHourVal > 0;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center group relative">
                      <div className="w-full rounded-t transition-all" style={{
                        height: `${barH}%`,
                        background: isPeak ? "#F59E0B" : "rgba(245,158,11,0.3)",
                      }} />
                      {i % 4 === 0 && (
                        <span className="text-[9px] text-white/20 mt-1">{h.hour}:00</span>
                      )}
                      <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white whitespace-nowrap z-10">
                        {h.hour}:00 — {h.listeners} listeners
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
